import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { makeRequest2 } from '../../../../../utils/makeRequest';
import { toastify } from '../../../../../utils/toast';
import { useFacilityStore } from '../../../../../app/z-store/store';
import { getItem } from "../../../../../utils/localStorage";
import { getBudgets, addBudget, GET_DEPARTMENT_URL,approveBudget } from "../../../../../utils/urls";

function BudgetTab() {
    // State variables
    const selectedFacilityId = useFacilityStore((state) => state.facilityId);
    const [budgets, setBudgets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [showAddModal, setShowAddModal] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [departments, setDepartments] = useState([]);
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const navigate = useNavigate();

    // Form state
    const [budgetForm, setBudgetForm] = useState({
        budgetName: '',
        allocation: '',
        startDate: '',
        endDate: '',
        department: '',
        overruns: false,
        userName: ''
    });

    // Comments dialog state
    const [commentsDialogVisible, setCommentsDialogVisible] = useState(false);
    const [comments, setComments] = useState('');
    const [selectedBudgetId, setSelectedBudgetId] = useState(null);
    const [approvalAction, setApprovalAction] = useState(null);

    // Status options
    const statusOptions = [
        { label: 'ALL', value: 'ALL' },
        { label: 'PENDING', value: 'pending' },
        { label: 'IN PROGRESS', value: 'in_progress' },
        { label: 'APPROVED', value: 'approved' },
        { label: 'REJECTED', value: 'rejected' }
    ];

    // Fetch departments from facility
    const fetchDepartments = async () => {
        if (!selectedFacilityId) return;
        
        try {
            setLoadingDepartments(true);
            const response = await makeRequest2(
                `${GET_DEPARTMENT_URL}/${selectedFacilityId}`,
                'GET'
            );
            
            if (response.success && response.data) {
                const departmentData = response.data.data || response.data;
                const departmentsArray = Array.isArray(departmentData) 
                    ? departmentData 
                    : [departmentData];
                setDepartments(departmentsArray);
            } else {
                console.error("Failed to fetch departments:", response.error);
                setDepartments([]);
            }
        } catch (error) {
            console.error("Error fetching departments:", error);
            setDepartments([]);
            toastify('Error fetching departments', 'error');
        } finally {
            setLoadingDepartments(false);
        }
    };

    // Get current user when component mounts
    useEffect(() => {
        const getCurrentUser = async () => {
            try {
                const currentUserValue = await getItem("APPUSER");
                let parsedUser;

                if (typeof currentUserValue === "string") {
                    try {
                        parsedUser = JSON.parse(currentUserValue);
                    } catch (err) {
                        console.error("Error parsing APPUSER:", err);
                    }
                } else {
                    parsedUser = currentUserValue;
                }

                if (parsedUser?.user) {
                    setCurrentUser(parsedUser.user);
                    setBudgetForm(prev => ({
                        ...prev,
                        userName: parsedUser.user.fullName
                    }));
                }
            } catch (err) {
                console.log("Error getting user:", err.message);
            }
        };

        getCurrentUser();
    }, []);

    // Fetch budgets and departments when facility changes
    useEffect(() => {
        if (selectedFacilityId) {
            fetchBudgets();
            fetchDepartments();
        }
    }, [selectedFacilityId]);

    // Fetch budgets from API
    const fetchBudgets = async () => {
        try {
            setLoading(true);
            const response = await makeRequest2(
                `${getBudgets}/${selectedFacilityId}`,
                'GET'
            );
            if (response.success) {
                setBudgets(response.data?.budgets || []);
            } else {
                console.error("Failed to fetch budgets:", response.error);
                setBudgets([]);
            }
        } catch (error) {
            console.error("Error fetching budgets:", error);
            setBudgets([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (field, value) => {
        setBudgetForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!budgetForm.budgetName || !budgetForm.allocation || !budgetForm.startDate ||
            !budgetForm.endDate || !budgetForm.department) {
            toastify('Please fill in all required fields', 'error');
            return;
        }

        // Validate dates
        if (new Date(budgetForm.startDate) >= new Date(budgetForm.endDate)) {
            toastify('End date must be after start date', 'error');
            return;
        }

        try {
            setLoading(true);
            const response = await makeRequest2(
                `${addBudget}/${selectedFacilityId}`,
                'POST',
                {
                    name: budgetForm.budgetName,
                    allocation: parseFloat(budgetForm.allocation),
                    startDate: budgetForm.startDate,
                    endDate: budgetForm.endDate,
                    department: budgetForm.department,
                    overruns: budgetForm.overruns,
                    userName: budgetForm.userName
                }
            );

            if (response.success) {
                toastify('Budget added successfully', 'success');
                setShowAddModal(false);
                setBudgetForm({
                    budgetName: '',
                    allocation: '',
                    startDate: '',
                    endDate: '',
                    department: '',
                    overruns: false,
                    userName: currentUser?.fullName || ''
                });
                fetchBudgets();
            } else {
                toastify(response.error || 'Failed to add budget', 'error');
            }
        } catch (error) {
            console.error("Error adding budget:", error);
            toastify('Error adding budget', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Check if current user is an approver for a specific budget
    const isUserApprover = (budget) => {
        if (!currentUser?.userId || !Array.isArray(budget.approvals)) return false;

        const step = budget.approvals.find(s => s.stepNumber === budget.currentStep);
        if (!step || !Array.isArray(step.approvers)) return false;

        return step.approvers.some(approver =>
            String(approver.userId) === currentUser.userId &&
            approver.status === 'pending'
        );
    };

    // Check if budget has been approved by the current user
    const hasUserApproved = (budget) => {
        if (!currentUser?.userId || !budget.approvals) return false;

        for (const step of budget.approvals) {
            if (!Array.isArray(step.approvers)) continue;

            const hasApproved = step.approvers.some(approver =>
                String(approver.userId) === currentUser.userId &&
                approver.status === 'approved'
            );

            if (hasApproved) return true;
        }

        return false;
    };

    // Open comments dialog for approval/rejection
    const openCommentsDialog = (id, action) => {
        setSelectedBudgetId(id);
        setApprovalAction(action);
        setComments('');
        setCommentsDialogVisible(true);
    };

    // Submit approval/rejection with comments
    const submitApprovalAction = async () => {
        if (!selectedBudgetId || !approvalAction) return;

        try {
            setLoading(true);
            setCommentsDialogVisible(false);

            const endpoint = `${approveBudget}/${selectedFacilityId}/${selectedBudgetId}`;
            const approve = approvalAction === 'approve';

            const response = await makeRequest2(
                endpoint,
                'PUT',
                {
                    approve,
                    comments,
                    userId: currentUser.userId
                }
            );

            if (response.success) {
                toastify(
                    response.message ||
                    `Budget ${approve ? 'approved' : 'rejected'} successfully`,
                    'success'
                );
                fetchBudgets();
            } else {
                toastify(
                    response.error ||
                    `Failed to ${approve ? 'approve' : 'reject'} budget`,
                    'error'
                );
            }
        } catch (error) {
            console.error("Action error:", error);
            toastify(`Error ${approvalAction === 'approve' ? 'approving' : 'rejecting'} budget`, 'error');
        } finally {
            setLoading(false);
            setSelectedBudgetId(null);
            setApprovalAction(null);
        }
    };

    // Filter budgets based on search and status
    const filteredBudgets = Array.isArray(budgets) ? budgets.filter((budget) => {
        const budgetName = (budget.budgetName || '').toLowerCase();
        const department = (budget.department || '').toLowerCase();
        const userName = (budget.userName || '').toLowerCase();

        const textFilterPassed =
            budgetName.includes(searchText.toLowerCase()) ||
            department.includes(searchText.toLowerCase()) ||
            userName.includes(searchText.toLowerCase());

        const statusFilterPassed =
            statusFilter === 'ALL' || (budget.approvalStatus || '').toLowerCase() === statusFilter;

        return textFilterPassed && statusFilterPassed;
    }) : [];

    // Format status badge
    const formatStatusBadge = (rowData) => {
        const statusStyles = {
            approved: { color: '#28a745', backgroundColor: '#e9fbe9' },
            pending: { color: '#ffc107', backgroundColor: '#fff8e1' },
            in_progress: { color: '#17a2b8', backgroundColor: '#e1f7fa' },
            rejected: { color: '#dc3545', backgroundColor: '#fde9e9' }
        };

        const status = (rowData.approvalStatus || '').toLowerCase();
        const style = statusStyles[status] || { color: '#6c757d', backgroundColor: '#f1f1f1' };

        return (
            <span
                style={{
                    ...style,
                    padding: '0.5em 1em',
                    borderRadius: '12px',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    display: 'inline-block',
                    textTransform: 'uppercase'
                }}
            >
                {rowData.approvalStatus?.replace('_', ' ')}
            </span>
        );
    };

    // Format currency
    const formatCurrency = (rowData) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'KES'
        }).format(rowData.allocation);
    };

    // Format date
    const formatDate = (rowData, field) => {
        return new Date(rowData[field]).toLocaleDateString();
    };

const actionTemplate = (rowData) => {
    const isApprover = isUserApprover(rowData);
    const hasApproved = hasUserApproved(rowData);
    const isPending = (rowData.approvalStatus || '').toLowerCase() === 'pending' ||
        (rowData.approvalStatus || '').toLowerCase() === 'in_progress';

    // Navigation function to view budget
    const handleViewBudget = () => {
        navigate(`/facility/expense_and_budget_mgmnt/components/budget_tabs/view_budget/${rowData._id}`);
    };

    // If user has already approved, show only view button
    if (hasApproved) {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    tooltip="View"
                    onClick={handleViewBudget}
                />
            </div>
        );
    }

    // If user is an approver and budget is in pending approval state
    if (isApprover && isPending) {
        return (
            <div className="d-flex gap-2">
                <Button
                    icon="ti ti-eye"
                    className="p-button-rounded p-button-info"
                    tooltip="View"
                    onClick={handleViewBudget}
                />
                <Button
                    icon="ti ti-check"
                    className="p-button-rounded p-button-success"
                    onClick={() => openCommentsDialog(rowData._id, 'approve')}
                    tooltip="Approve"
                />
                <Button
                    icon="ti ti-x"
                    className="p-button-rounded p-button-danger"
                    onClick={() => openCommentsDialog(rowData._id, 'reject')}
                    tooltip="Reject"
                />
            </div>
        );
    }

    // For regular users or when budget is not pending
    return (
        <div className="d-flex gap-2">
            <Button
                icon="ti ti-eye"
                className="p-button-rounded p-button-info"
                tooltip="View"
                onClick={handleViewBudget}
            />
            <Button
                icon="ti ti-edit"
                className="p-button-rounded p-button-warning"
                tooltip="Edit"
                onClick={() => console.log('Edit budget:', rowData._id)}
                disabled={(rowData.approvalStatus || '').toLowerCase() !== 'pending'}
            />
        </div>
    );
};

    // Dialog footer for comments
    const commentsDialogFooter = (
        <div>
            <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => setCommentsDialogVisible(false)}
            />
            <Button
                label={approvalAction === 'approve' ? 'Approve' : 'Reject'}
                icon={approvalAction === 'approve' ? 'pi pi-check' : 'pi pi-times'}
                className={approvalAction === 'approve' ? 'p-button-success' : 'p-button-danger'}
                onClick={submitApprovalAction}
            />
        </div>
    );

    // If no facility is selected
    if (!selectedFacilityId) {
        return (
            <div className="card">
                <div className="card-body text-center">
                    Please select a facility to view budgets.
                </div>
            </div>
        );
    }

    return (
        <div className="row">
            <div className="col-lg-12 col-xxl-12">
                <div className="card">
                    <div className="card-body position-relative">
                        <ConfirmDialog />

                        {/* Header with Add New Budget button on left and search bar on right */}
                        <div className="row mb-3">
                                <div className="col-md-4">
                                <input
                                    className="form-control"
                                    placeholder="Search budgets..."
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                             <div className="col-md-8 text-end">
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => setShowAddModal(true)}
                                >
                                    Add New Budget
                                </button>
                            </div>
                        </div>

                        {/* Status Filter Buttons */}
                        <div className="row mb-3">
                            <div className="col-12">
                                <div className="d-flex flex-wrap gap-2">
                                    {statusOptions.map(option => (
                                        <button
                                            key={option.value}
                                            className={`btn ${statusFilter === option.value ? 'btn-primary' : 'btn-outline-secondary'} text-uppercase`}
                                            onClick={() => setStatusFilter(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Data Table */}
                        <div className="dt-responsive table-responsive">
                            <DataTable
                                value={filteredBudgets}
                                emptyMessage="No budgets found."
                                sortMode="multiple"
                                paginator
                                rows={10}
                                stripedRows
                                loading={loading}
                                tableStyle={{ minWidth: "50rem" }}
                                stateStorage="session"
                                stateKey="budget-state"
                            >
                                <Column
                                    field="budgetName"
                                    header="BUDGET NAME"
                                    sortable
                                />
                                <Column
                                    field="allocation"
                                    header="ALLOCATION"
                                    body={formatCurrency}
                                />
                                <Column
                                    field="department"
                                    header="DEPARTMENT"
                                />
                                <Column
                                    field="startDate"
                                    header="START DATE"
                                    body={(rowData) => formatDate(rowData, 'startDate')}
                                    sortable
                                />
                                <Column
                                    field="endDate"
                                    header="END DATE"
                                    body={(rowData) => formatDate(rowData, 'endDate')}
                                    sortable
                                />
                                <Column
                                    field="userName"
                                    header="CREATED BY"
                                    sortable
                                />
                                <Column
                                    field="approvalStatus"
                                    header="Approval Status"
                                    body={formatStatusBadge}
                                    sortable
                                />
                                <Column
                                    header="ACTIONS"
                                    body={actionTemplate}
                                    style={{ width: '12rem' }}
                                />
                            </DataTable>
                        </div>

                        {/* Add Budget Modal */}
                        <Dialog
                            header="Add New Budget"
                            visible={showAddModal}
                            style={{ width: '600px' }}
                            onHide={() => setShowAddModal(false)}
                            footer={
                                <div>
                                    <Button
                                        label="Cancel"
                                        icon="pi pi-times"
                                        className="p-button-text"
                                        onClick={() => setShowAddModal(false)}
                                    />
                                    <Button
                                        label="Save Budget"
                                        icon="pi pi-check"
                                        className="p-button-primary"
                                        onClick={handleSubmit}
                                        loading={loading}
                                    />
                                </div>
                            }
                        >
                            <form onSubmit={handleSubmit}>
                                <div className="mb-3">
                                    <label className="form-label">Budget Name *</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={budgetForm.budgetName}
                                        onChange={(e) => handleInputChange('budgetName', e.target.value)}
                                        required
                                    />
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Allocation Amount *</label>
                                    <input
                                        type="number"
                                        className="form-control"
                                        value={budgetForm.allocation}
                                        onChange={(e) => handleInputChange('allocation', e.target.value)}
                                        min="0"
                                        step="0.01"
                                        required
                                    />
                                </div>

                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">Start Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={budgetForm.startDate}
                                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <label className="form-label">End Date *</label>
                                            <input
                                                type="date"
                                                className="form-control"
                                                value={budgetForm.endDate}
                                                onChange={(e) => handleInputChange('endDate', e.target.value)}
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Department *</label>
                                    <select
                                        className="form-select"
                                        value={budgetForm.department}
                                        onChange={(e) => handleInputChange('department', e.target.value)}
                                        required
                                        disabled={loadingDepartments}
                                    >
                                        <option value="">
                                            {loadingDepartments ? 'Loading departments...' : 'Select Department'}
                                        </option>
                                        {departments.map(dept => (
                                            <option key={dept._id} value={dept.name}>
                                                {dept.name}
                                            </option>
                                        ))}
                                    </select>
                                    {departments.length === 0 && !loadingDepartments && (
                                        <small className="text-muted">
                                            No departments available. Please add departments first.
                                        </small>
                                    )}
                                </div>

                                <div className="mb-3">
                                    <label className="form-label">Created By</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={budgetForm.userName}
                                        onChange={(e) => handleInputChange('userName', e.target.value)}
                                        readOnly
                                    />
                                </div>

                                <div className="mb-3">
                                    <div className="form-check">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={budgetForm.overruns}
                                            onChange={(e) => handleInputChange('overruns', e.target.checked)}
                                        />
                                        <label className="form-check-label">
                                            Allow Overruns
                                        </label>
                                    </div>
                                </div>
                            </form>
                        </Dialog>

                        <Dialog
                            header={`${approvalAction === 'approve' ? 'Approve' : 'Reject'} Budget`}
                            visible={commentsDialogVisible}
                            style={{ width: '500px' }}
                            footer={commentsDialogFooter}
                            onHide={() => setCommentsDialogVisible(false)}
                        >
                            <div className="field">
                                <label htmlFor="comments">Comments (Optional)</label>
                                <InputTextarea
                                    id="comments"
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                    rows={5}
                                    cols={30}
                                    className="w-100"
                                    placeholder="Enter any comments regarding your decision..."
                                />
                            </div>
                        </Dialog>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default BudgetTab;